import React from "react";
import type { ExpiryItem } from "../hooks/useExpiryItems";
import { getItemStatus } from "../utils/expiryUtils";
import { ACTION_BTN_BASE } from "../constants/expiry";

// ─── Hover modifier classes per action ───────────────────────────────────────
const HOVER = {
  addFields: "hover:bg-[#EFF6FF] hover:text-[#2563EB] hover:border-[#BFDBFE]",
  renew: "hover:bg-[#F0FDF4] hover:text-[#15803D] hover:border-[#86EFAC]",
  sendMail: "hover:bg-[#FFFBEB] hover:text-[#B45309] hover:border-[#FDE68A]",
} as const;

// ─── Inline SVG helpers ───────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v16m8-8H4"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const MailIcon = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

interface ActionCellProps {
  item: ExpiryItem;
  onAddFields?: (item: ExpiryItem) => void;
  onRenew?: (item: ExpiryItem) => void;
  onSendReminder: (item: ExpiryItem) => void;
}

/**
 * Renders a contextual action button in the "Action" column based on the
 * item's current status:
 *
 *  • missing   → "Add Fields"  (blue hover)
 *  • expired   → "Renew"       (green hover)
 *  • high-risk / upcoming → "Send Mail"  (amber hover)
 *  • active    → "—"           (no action needed)
 *
 * Uses the shared ACTION_BTN_BASE class so the style stays in sync with the
 * BulkActionBar buttons.
 */
const ActionCell: React.FC<ActionCellProps> = ({
  item,
  onAddFields,
  onRenew,
  onSendReminder,
}) => {
  const { status } = getItemStatus(item);

  if (status === "missing") {
    if (!onAddFields) return <span className="text-xs text-[#9CA3AF]">—</span>;
    return (
      <button
        onClick={() => onAddFields(item)}
        className={`${ACTION_BTN_BASE} ${HOVER.addFields}`}
        title="Add missing expiry date and other fields"
      >
        <PlusIcon />
        Add Fields
      </button>
    );
  }

  if (status === "expired") {
    if (!onRenew) return <span className="text-xs text-[#9CA3AF]">—</span>;
    return (
      <button
        onClick={() => onRenew(item)}
        className={`${ACTION_BTN_BASE} ${HOVER.renew}`}
        title="Set a new expiry date to renew this document"
      >
        <RefreshIcon />
        Renew
      </button>
    );
  }

  if (status === "high-risk" || status === "upcoming") {
    return (
      <button
        onClick={() => onSendReminder(item)}
        className={`${ACTION_BTN_BASE} ${HOVER.sendMail}`}
        title="Send a reminder email to document owners"
      >
        <MailIcon />
        Send Mail
      </button>
    );
  }

  // Active — no urgent action required
  return <span className="text-xs text-[#9CA3AF]">—</span>;
};

export default ActionCell;
